import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { ScatterChart } from '@mui/x-charts/ScatterChart';

import { useGetPriceAnalysisMutation } from 'services/api/cars.api';
import numberFormat from 'services/format/numberFormat';
import priceFormat from 'services/format/priceFormat';
import type { CarComparisonCar } from 'services/types/car';
import normalCDF from 'utils/normalCDF';

export const capitalize = (s: unknown): string => {
  if (!s) {
    return '';
  }
  const arr = String(s).trim().split(' ');
  return arr.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

interface PriceAnalysisModalProps {
  open: boolean;
  onClose: () => void;
  carId: number;
}

// Custom tooltip content component
const ChartTooltipContent = ({
  itemData,
  series,
}: {
  itemData: { dataIndex: number };
  series: {
    id: string;
    data: {
      id: string;
      x: number;
      y: number;
    }[];
  };
}) => {
  const { id, x, y } = series.data[itemData.dataIndex];
  // Combined value are always negative.
  const xDisplay = x < 0 ? `${numberFormat(-10 * x)} (Valor combinado entre km y años de uso)` : numberFormat(x);

  // For regression series
  if (series.id === 'regression') {
    return (
      <Box sx={{ p: 1, backgroundColor: 'white' }}>
        <Typography variant="body2">Línea de regresión</Typography>
        <Typography variant="body2">Valor: {xDisplay}</Typography>
        <Typography variant="body2">Precio: {priceFormat.format(y)}</Typography>
      </Box>
    );
  }

  // For current car
  if (series.id === 'current') {
    return (
      <Box sx={{ p: 1, backgroundColor: 'white' }}>
        <Typography variant="body2">Auto actual</Typography>
        <Typography variant="body2">Valor: {xDisplay}</Typography>
        <Typography variant="body2">Precio: {priceFormat.format(y)}</Typography>
      </Box>
    );
  }

  // For auto and manual cars
  const transmissionText = series.id === 'cars-automatic' ? 'Automático' : 'Manual';

  return (
    <Box sx={{ p: 1, backgroundColor: 'white' }}>
      <Typography variant="body2">ID: {id}</Typography>
      <Typography variant="body2">Valor: {xDisplay}</Typography>
      <Typography variant="body2">Precio: {priceFormat.format(y)}</Typography>
      <Typography variant="body2">Transmisión: {transmissionText}</Typography>
    </Box>
  );
};

type ComparisonType = 'general' | 'same-transmission' | 'most-similar' | 'db' | 'chileautos' | 'mercadolibre';

type ComparisonCarWithXValue = CarComparisonCar & { x: number };
type Attribute = 'km' | 'usageYears' | 'combined';
type SimplifiedCar = { km?: number | null; usageYears?: number | null };

const getXAxisLabel = (attr: Attribute): string => {
  switch (attr) {
    case 'km':
      return 'Kilometraje';
    case 'usageYears':
      return 'Años de uso';
    case 'combined':
      return 'Combinación (Km y Años)';
    default:
      return 'Kilometraje';
  }
};

const PriceAnalysisModal: React.FC<PriceAnalysisModalProps> = ({ open, onClose, carId }) => {
  const [comparison, setComparison] = useState<ComparisonType>('general');
  const [getPriceAnalysis, { data, isLoading }] = useGetPriceAnalysisMutation();
  const [attribute, setAttribute] = useState<Attribute>('combined');
  const [carPrice, setCarPrice] = useState<number>(0);
  const [carKm, setCarKm] = useState<number>(0);
  const [carUsageYears, setCarUsageYears] = useState<number>(0);
  const [customAnalysis, setCustomAnalysis] = useState<boolean>(false);

  useEffect(() => {
    getPriceAnalysis({ carId }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error fetching price analysis', error);
    });
  }, [carId]);

  useEffect(() => {
    if (data?.car) {
      setCarPrice(data.car.price);
      setCarKm(data.car.km);
      setCarUsageYears(data.car.usageYears || 0);
    }
  }, [data?.car]);

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Análisis de precio de auto</DialogTitle>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const results = data?.comparison.find((c) => c.source === comparison);
  if (!data || !results) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Análisis de precio de auto</DialogTitle>
        <DialogContent>
          <Typography variant="body1">No se pudo obtener el análisis de precio de auto</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const calculateXValue = (car: SimplifiedCar): number => {
    switch (attribute) {
      case 'usageYears':
        return car.usageYears || 0;
      case 'combined':
        return (
          results.multiExponentialRegression.bKm * (car.km || 0) +
          results.multiExponentialRegression.bUsageYears * (car.usageYears || 0)
        );
      case 'km':
      default:
        return car.km || 0;
    }
  };

  const calculateRegressionY = (xValue: number): number => {
    if (attribute === 'km') {
      return results.exponentialRegressionKm.a * Math.exp(results.exponentialRegressionKm.b * xValue);
    }
    if (attribute === 'usageYears') {
      return results.exponentialRegressionUsageYears.a * Math.exp(results.exponentialRegressionUsageYears.b * xValue);
    }
    return results.multiExponentialRegression.a * Math.exp(xValue);
  };

  let predictedPriceText = 'No disponible';
  if (results?.predictedPrice) {
    predictedPriceText = `Predicción de precio: ${priceFormat.format(results.predictedPrice)}`;
    const percentile = normalCDF(results.priceDeviation / results.standardDeviationOfResiduals) * 100;
    predictedPriceText += ` (Precio actual tiene un ${numberFormat(
      Math.round(results.priceDeviation * 100)
    )}% de diferencia con el precio predicho, debería ser ${percentile < 50 ? 'más barato' : 'más caro'} que el ${
      percentile < 50 ? (100 - percentile).toFixed(1) : percentile.toFixed(1)
    }% de los autos de este tipo con kilometraje similar)`;
  }

  let correlationExponentialText = '';
  correlationExponentialText = `Confiabilidad de la curva de predicción: ${results.multiExponentialRegression.rSquared.toFixed(
    3
  )}`;
  if (results.multiExponentialRegression.rSquared > 0.5 && results.cars.length > 6) {
    correlationExponentialText += ' (alta correlación con varios autos, confiable para predecir el precio)';
  } else if (results.multiExponentialRegression.rSquared > 0.2 && results.cars.length > 3) {
    correlationExponentialText += ' (correlación media, un poco confiable para predecir el precio)';
  } else {
    correlationExponentialText += ' (correlación mala o muy pocos autos, no confiable para predecir el precio)';
  }

  // Extract automatic and manual cars
  const manualCars: ComparisonCarWithXValue[] =
    results.cars
      .filter((c) => c.transmission !== 'AUTOMATICA')
      .map((c) => ({
        ...c,
        x: calculateXValue(c),
      })) || [];
  const automaticCars: ComparisonCarWithXValue[] =
    results.cars
      .filter((c) => c.transmission === 'AUTOMATICA')
      .map((c) => ({
        ...c,
        x: calculateXValue(c),
      })) || [];

  // Handle item click to open the link
  const handleItemClick = (_: unknown, item: unknown) => {
    if (!item || !results) return;

    const { seriesId, dataIndex } = item as { seriesId: string; dataIndex: number };
    let link;
    let id;

    if (seriesId === 'cars-manual' && manualCars[dataIndex]) {
      link = manualCars[dataIndex].link;
      id = manualCars[dataIndex].id;
    } else if (seriesId === 'cars-automatic' && automaticCars[dataIndex]) {
      link = automaticCars[dataIndex].link;
      id = automaticCars[dataIndex].id;
    }

    if (id) {
      window.open(`/cars?id=${id}&analyzePrice=true`, '_blank');
    } else if (link) {
      window.open(link, '_blank');
    }
  };

  const generalCarsLength = data.comparison.find((comp) => comp.source === 'general')?.cars.length || 0;
  const sameTransmissionCarsLength =
    data.comparison.find((comp) => comp.source === 'same-transmission')?.cars.length || 0;
  const mostSimilarCarsLength = data.comparison.find((comp) => comp.source === 'most-similar')?.cars.length || 0;
  const dbCarsLength = data.comparison.find((comp) => comp.source === 'db')?.cars.length || 0;
  const mercadolibreCarsLength = data.comparison.find((comp) => comp.source === 'mercadolibre')?.cars.length || 0;
  const chileautosCarsLength = data.comparison.find((comp) => comp.source === 'chileautos')?.cars.length || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          m: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
        },
      }}
    >
      <DialogTitle>
        Análisis de precio de {data.car.brand} {data.car.model}{' '}
        <a href={data?.car.link} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '10px' }}>
          Ver auto
        </a>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 3,
          overflowX: 'auto',
          '& > *': {
            minWidth: { xs: '800px', sm: 'auto' },
          },
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          Precio: {priceFormat.format(data?.car.price || 0)}
        </Typography>
        <Typography variant="body1">Concesionario: {data.car.businessUnit?.name}</Typography>
        <Typography variant="body1">Kilometraje: {numberFormat(data?.car.km || 0)}</Typography>
        <Typography variant="body1">Transmisión: {data?.car.transmission}</Typography>
        <Typography variant="body1">Combustible: {data?.car.fuelType}</Typography>
        <Typography variant="body1">
          Año: {data?.car.year} ({data?.car.usageYears?.toFixed(1)} años de uso)
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3, mb: 2 }}>
          <Typography variant="body2">Analizar auto fuera del stock:</Typography>
          <Switch
            checked={customAnalysis}
            onChange={(event) => {
              const isCustom = event.target.checked;
              setCustomAnalysis(isCustom);
              if (!isCustom && data?.car) {
                // Reset to original car data when turning off custom analysis
                setCarPrice(data.car.price);
                setCarKm(data.car.km);
                setCarUsageYears(data.car.usageYears || 0);
              }
              if (isCustom) {
                setCarUsageYears(Math.round(data.car.usageYears || 0));
              }
            }}
          />
        </Stack>

        {customAnalysis && (
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }}>
            <TextField
              label="Precio"
              type="number"
              value={carPrice}
              onChange={(e) => setCarPrice(Number(e.target.value))}
              InputProps={{
                startAdornment: '$',
                inputProps: { step: 500_000, min: 1_000_000 },
              }}
              fullWidth
            />
            <TextField
              label="Kilometraje"
              type="number"
              value={carKm}
              onChange={(e) => setCarKm(Number(e.target.value))}
              inputProps={{ step: 10_000, min: 0 }}
              fullWidth
            />
            <TextField
              label="Año"
              type="number"
              value={2025 - carUsageYears}
              onChange={(e) => setCarUsageYears(2025 - Number(e.target.value))}
              inputProps={{ step: 1, min: 2000, max: 2025 }}
              fullWidth
            />
          </Box>
        )}

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2">Fuente de datos:</Typography>
          <ToggleButtonGroup
            value={comparison}
            exclusive
            onChange={(_, newValue) => {
              if (!newValue) {
                return;
              }
              setComparison(newValue as ComparisonType);
            }}
            size="small"
          >
            <ToggleButton value="general" disabled={generalCarsLength === 0}>
              Todos ({generalCarsLength})
            </ToggleButton>
            <ToggleButton value="same-transmission" disabled={sameTransmissionCarsLength === 0}>
              {data.car.transmission} ({sameTransmissionCarsLength})
            </ToggleButton>
            <ToggleButton value="most-similar" disabled={mostSimilarCarsLength === 0}>
              Más parecidos ({mostSimilarCarsLength})
            </ToggleButton>
            <ToggleButton value="db" disabled={dbCarsLength === 0}>
              Base de datos ({dbCarsLength})
            </ToggleButton>
            <ToggleButton value="chileautos" disabled={chileautosCarsLength === 0}>
              ChileAutos ({chileautosCarsLength})
            </ToggleButton>
            <ToggleButton value="mercadolibre" disabled={mercadolibreCarsLength === 0}>
              MercadoLibre ({mercadolibreCarsLength})
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Typography variant="body1">
          Precio promedio: {priceFormat.format(results.averagePrice)} ({results.cars.length} autos)
        </Typography>
        <Typography variant="body1">
          Regresiones:
          <ol>
            <li>
              <b>Kilometraje:</b> {priceFormat.format(Math.round(results.linearRegressionKm.intercept))} +{' '}
              {priceFormat.format(Math.round(results.linearRegressionKm.slope))}*KM. El precio baja{' '}
              {priceFormat.format(-Math.round(results.linearRegressionKm.slope) * 10_000)} por cada 10.000 km.
            </li>
            <li>
              <b>Años de uso:</b> {priceFormat.format(Math.round(results.linearRegressionUsageYears.intercept))} +{' '}
              {priceFormat.format(Math.round(results.linearRegressionUsageYears.slope))}*Años. El precio baja{' '}
              {priceFormat.format(-Math.round(results.linearRegressionUsageYears.slope))} por cada año de uso.
            </li>
            <li>
              <b>Kilometraje + años de uso:</b>{' '}
              {priceFormat.format(Math.round(results.multiLinearRegression.intercept))} +{' '}
              {priceFormat.format(Math.round(results.multiLinearRegression.slopeKm))}*KM +{' '}
              {priceFormat.format(Math.round(results.multiLinearRegression.slopeUsageYears))}*Años. El precio baja{' '}
              {priceFormat.format(-Math.round(results.multiLinearRegression.slopeKm) * 10_000)} por cada 10.000 km +{' '}
              {priceFormat.format(-Math.round(results.multiLinearRegression.slopeUsageYears))} por cada año de uso.
            </li>
          </ol>
        </Typography>
        {!customAnalysis && (
          <>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {predictedPriceText}
            </Typography>
            <Typography variant="body1">{correlationExponentialText}</Typography>
          </>
        )}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2">Tipo de análisis:</Typography>
          <ToggleButtonGroup
            value={attribute}
            exclusive
            onChange={(_, newValue) => {
              if (!newValue) return;
              setAttribute(newValue as 'km' | 'usageYears' | 'combined');
            }}
            size="small"
          >
            <ToggleButton value="km">Kilometraje</ToggleButton>
            <ToggleButton value="usageYears">Años de uso</ToggleButton>
            <ToggleButton value="combined">Km y Años</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {results.cars.length > 0 && (
          <Box sx={{ width: '100%', height: 400, mt: 2 }}>
            <ScatterChart
              xAxis={[
                {
                  id: attribute,
                  label: getXAxisLabel(attribute),
                  valueFormatter: (v: number) => {
                    if (attribute === 'combined') {
                      // Values generally go from -0.1 to 2, let's map them to 1 -> 20
                      return numberFormat(-10 * v);
                    }
                    return numberFormat(v);
                  },
                  reverse: attribute === 'combined',
                },
              ]}
              yAxis={[
                {
                  id: 'price',
                  label: '',
                  valueFormatter: (v) => priceFormat.format(v as number),
                },
              ]}
              margin={{ left: 80 }}
              series={[
                {
                  id: 'cars-manual',
                  data: manualCars.map((c) => ({
                    id: `${c.link}-${c.km}-${c.usageYears}`,
                    x: c.x,
                    y: c.price || 0,
                  })),
                  label: 'Autos manuales',
                  color: '#1f77b4',
                },
                {
                  id: 'cars-automatic',
                  data: automaticCars.map((c) => ({
                    id: `${c.link}-${c.km}-${c.usageYears}`,
                    x: c.x,
                    y: c.price || 0,
                  })),
                  label: 'Autos automáticos',
                  color: '#ffbb00',
                },
                {
                  id: 'current',
                  data: [
                    {
                      id: 'current',
                      x: calculateXValue({ km: carKm, usageYears: carUsageYears }),
                      y: carPrice,
                    },
                  ],
                  label: 'Auto actual',
                  color: '#ff0000',
                },
                {
                  id: 'regression',
                  data: (() => {
                    const minAttr = Math.min(
                      calculateXValue(data.car),
                      ...manualCars.map((c) => c.x),
                      ...automaticCars.map((c) => c.x)
                    );
                    const maxAttr = Math.max(
                      calculateXValue(data.car),
                      ...manualCars.map((c) => c.x),
                      ...automaticCars.map((c) => c.x)
                    );
                    // Create many points very close to each other to simulate a line
                    const points = [];
                    const numPoints = 80;
                    for (let i = 0; i <= numPoints; i += 1) {
                      const attr = minAttr + (maxAttr - minAttr) * (i / numPoints);
                      points.push({
                        id: `regression-${i}`,
                        x: attr,
                        y: Math.round(calculateRegressionY(attr)),
                      });
                    }
                    return points;
                  })(),
                  label: 'Línea de regresión',
                  color: '#00bb00',
                  markerSize: 1,
                },
              ]}
              height={400}
              tooltip={{
                trigger: 'item',
                itemContent: ChartTooltipContent,
              }}
              onItemClick={handleItemClick}
            />
            <Typography variant="body1" fontWeight="bold" mb={2}>
              <a href={data.car.link} target="_blank" rel="noopener noreferrer" style={{ marginRight: '5px' }}>
                Link
              </a>
              {capitalize(
                `${data.car.year} ${numberFormat(data.car.km)}km a ${priceFormat.format(data.car.price)}. ${
                  data.car.brand
                } ${data.car.model} ${data.car.version} ${data.car.transmission} ${data.car.fuelType}`
              )}
            </Typography>
            {[...results.cars]
              .sort((a, b) =>
                attribute === 'km' ? (a.km || 0) - (b.km || 0) : (a.usageYears || 0) - (b.usageYears || 0)
              )
              .map((c, index) => (
                <Stack direction="row" spacing={1} key={`car-${index + 1}-${c.link}`}>
                  {c.link && (
                    <a href={c.link} target="_blank" rel="noopener noreferrer">
                      Link
                    </a>
                  )}
                  <Typography variant="body1">
                    {capitalize(
                      `${c.year} ${numberFormat(c.km || 0)}km a ${priceFormat.format(c.price || 0)}. ${c.brand} ${
                        c.model
                      } ${c.transmission} ${c.fuelType}`
                    )}
                  </Typography>
                  {c.id && (
                    <Typography variant="body1">
                      <a href={`/cars?id=${c.id}&analyzePrice=true`} target="_blank" rel="noopener noreferrer">
                        Analizar precio
                      </a>
                    </Typography>
                  )}
                </Stack>
              ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PriceAnalysisModal;
